import { ArrowRight, type LucideIcon } from "lucide-react";
import Link from "next/link";

import { cn } from "@/lib/utils";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { CodeBlock } from "@/features/docs/components/code-block";
import type {
  ApiResponse,
  Constant,
  HttpMethod,
  MethodDocData,
  Parameter,
  Property,
} from "@/features/docs/types";

/**
 * The gradient accent rule that sits under every docs heading — the same
 * signature the /report and /send_money heroes use, so the docs read as part of
 * the same system rather than a separate site.
 *
 * The ramp runs brand-500 → brand-600 → green off tokens rather than hard-coded
 * hexes, so it re-tints with the theme instead of staying a dark-mode artefact
 * in light mode. `animated` drifts the band for the page-level hero only;
 * A short brand rule under a heading.
 *
 * This was a four-stop drifting ramp (primary → primary-text → info-text →
 * primary) until the 2026-08-03 Polymarket pass. Polymarket has no multi-hue
 * gradient anywhere in its chrome, so the rule is now solid brand blue and the
 * drift animation is gone — there is no ramp left to move. `animated` is kept
 * in the signature so the ~40 call sites don't all have to change; it is
 * deliberately inert.
 *
 * The middle stop was `--chart-4` (#9445d9) until 2026-07-31 — a purple that
 * appears nowhere else in the app and made every docs heading read off-brand.
 */
export function AccentRule({
  className,
  animated: _animated = false,
}: {
  className?: string;
  animated?: boolean;
}) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "h-[3px] w-14 rounded-full bg-primary",
        className,
      )}
    />
  );
}

/**
 * The primary CTA treatment established by the /report and /send_money
 * redesigns: a vertical primary gradient with an inset rim that lifts and
 * brightens on press. Exported so the docs reuse the exact CTA the rest of the
 * app already uses instead of a lookalike.
 *
 * NOTE: `report-form.tsx` still carries this as an inline literal. When a third
 * caller appears, promote it to a `cta` variant on Button and migrate both.
 */
export const primaryCta =
  "bg-primary font-semibold shadow-[0_1px_2px_0_rgb(0_0_0/0.2)] ring-1 ring-inset ring-border transition-[filter,box-shadow,transform] duration-150 hover:-translate-y-px hover:brightness-[1.08] active:translate-y-0 active:scale-[0.99]";

/**
 * Page title + optional description + badge, as a floating panel.
 *
 * The panel is the locked-in docs template (variant C / Runpod, 2026-08-03):
 * the page opens on a surface rather than on bare text, which is what lets the
 * cards and code blocks below it read as a set of panels instead of a stack of
 * unrelated blocks. Replaces the old bare title + gradient `AccentRule` rule
 * (the rule survives for section headings in the v1 renderer).
 *
 * `hero` is a top-level landing statement: a heavier, larger title over a soft
 * primary orb. Reserved for the two "Getting started" destinations (/docs and
 * /docs/search), which have no competing content above them. Reference and
 * guide pages pass it off and keep the quieter title, so the hero treatment
 * stays a signal that you're at an entry point rather than page decoration.
 */
export function PageHeader({
  title,
  description,
  badge,
  hero = false,
  className,
}: {
  title: string;
  description?: string;
  badge?: string;
  hero?: boolean;
  /** Override the default bottom margin. `cn` is twMerge-backed, so a `mb-*`
   * here replaces the default rather than fighting it. */
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-hairline bg-card",
        hero ? "mb-12 p-9" : "mb-10 p-7",
        className,
      )}
    >
      {/* A blurred brand-blue bloom used to sit behind the hero title here.
       * Removed in the 2026-08-03 Polymarket pass — Polymarket's panels are
       * flat fills with a hairline, with no light bleeding behind them. */}
      <div className="relative flex flex-wrap items-center gap-3">
        <h1
          className={cn(
            "font-extrabold leading-[1.12] tracking-[-0.035em]",
            hero ? "text-[2.75rem] sm:text-[3.25rem]" : "text-[2.125rem]",
          )}
        >
          {title}
        </h1>
        {badge ? (
          <span className="rounded-full bg-primary/15 px-2.5 py-1 text-[0.6875rem] font-bold uppercase tracking-[0.1em] text-primary-text">
            {badge}
          </span>
        ) : null}
      </div>
      {description ? (
        <p
          className={cn(
            "relative mt-3.5 max-w-[62ch] leading-relaxed text-muted-foreground",
            hero && "text-lg",
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A section heading inside a page — the accent bar is the marker, matching the
 * one on the sidebar's section labels.
 */
export function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-4 flex items-center gap-2.5 text-lg font-bold tracking-[-0.02em] text-foreground">
      <span
        aria-hidden="true"
        className="h-4 w-1 rounded-full bg-primary"
      />
      {children}
    </h2>
  );
}

/** An identifier inside prose — `agent`, a method name, a flag. */
export function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded-md bg-primary/10 px-1.5 py-0.5 font-mono text-[0.8125rem] font-medium text-primary-text">
      {children}
    </code>
  );
}

/** A body paragraph, bounded to a reading measure. */
export function Prose({ children }: { children: React.ReactNode }) {
  return (
    <p className="max-w-[68ch] leading-relaxed text-muted-foreground">
      {children}
    </p>
  );
}

/** A titled content panel — the same surface as `PageHeader`, one level down. */
export function DocPanel({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4 rounded-2xl border border-hairline bg-card p-7">
      <h2 className="text-xl font-bold text-foreground">{heading}</h2>
      {children}
    </section>
  );
}

/**
 * A neutral titled panel: a heading over free content, on the same flat card as
 * the rest of the docs.
 *
 * For reference material that is NOT an advisory — the Error Handling entries
 * on the API page, the two "when to use which" lists on the helpers page. Both
 * had been ported as tinted `Callout`s, which reads as something to act on, and
 * on a two-sided comparison a tint on both halves says nothing while a tint on
 * one implies a recommendation the legacy page never made.
 *
 * Takes arbitrary children rather than a string: the callers need a paragraph
 * and a bulleted list respectively.
 */
export function NotePanel({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-hairline bg-card p-5">
      <h3 className="mb-1.5 font-bold text-foreground">{heading}</h3>
      <div className="text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </div>
  );
}

/** A stack of `LinkCard`s. */
export function CardStack({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3">{children}</div>;
}

/**
 * A full-width row linking to another page: title, one line of description, and
 * an arrow that fills in on hover. Used for the Quick Links and the guide /
 * reference card groups, where the list is scanned top-to-bottom — a grid of
 * boxes there makes the reader hunt in two dimensions for a linear list.
 *
 * The arrow is `ArrowRight`, not `ArrowUpRight`: every one of these rows links
 * deeper into the docs, and the diagonal glyph is the conventional "leaves this
 * site" marker. It also matches the reference, whose equivalent card carries a
 * plain forward arrow.
 */
export function LinkCard({
  title,
  description,
  href,
}: {
  title: string;
  description: string;
  href: string;
}) {
  return (
    <Link
      href={href}
      className="group flex items-start justify-between gap-4 rounded-2xl border border-hairline bg-card p-5 transition-all duration-200 hover:border-primary/50 hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <div>
        <h3 className="font-bold text-foreground transition-colors group-hover:text-primary-text">
          {title}
        </h3>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full bg-elevated text-muted-foreground transition-all duration-200 group-hover:bg-primary/15 group-hover:text-primary-text"
      >
        <ArrowRight className="size-4" />
      </span>
    </Link>
  );
}

/**
 * Per-card accent hues. Each card owns one hue across its icon tile, its hover
 * rim, and its lift shadow — the colour is what makes four text blocks read as
 * four distinct destinations.
 *
 * Keys are named for the colour they actually paint. They used to be
 * `indigo`/`violet`/`teal` while pointing at blue/purple/green, and `violet`
 * carried `--chart-4` (#9445d9) — the only purple in the app, which made /docs
 * read as a different product from every other page. The two blues are the
 * brand ramp's own steps (brand-500 and brand-600), so the set stays
 * distinguishable without leaving the Polymarket palette.
 */
const NAV_CARD_ACCENTS = {
  brand: "var(--chart-1)", // #0093fd — brand-500
  sky: "var(--info-text)", // #26a3fd — brand-600
  green: "var(--chart-2)", // #3db468
  amber: "var(--chart-3)", // #f7d022
} as const;

export type NavCardAccent = keyof typeof NAV_CARD_ACCENTS;

/** A link card to another docs section. */
export function NavCard({
  title,
  description,
  href,
  icon: Icon,
  accent = "brand",
}: {
  title: string;
  description: string;
  href: string;
  icon?: LucideIcon;
  accent?: NavCardAccent;
}) {
  const hue = NAV_CARD_ACCENTS[accent];
  return (
    <Link
      href={href}
      // The accent hue is threaded through as a CSS var so the icon tile, rim,
      // and shadow below can all reference one value instead of repeating a
      // colour in three arbitrary-value classes.
      style={{ "--card-accent": hue } as React.CSSProperties}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-hairline bg-card p-6 transition-all duration-200 hover:border-[hsl(var(--card-accent)/0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      {/* The hue used to pool as a blurred radial wash in the top-right corner
       * on hover. Dropped in the 2026-08-03 Polymarket pass; the card now
       * signals hover with its border alone, as Polymarket's cards do. */}
      {Icon ? (
        <span
          aria-hidden="true"
          className="relative mb-4 inline-flex size-11 items-center justify-center rounded-xl bg-[hsl(var(--card-accent)/0.15)] text-[hsl(var(--card-accent))]"
        >
          <Icon className="size-5" strokeWidth={2} />
        </span>
      ) : null}
      <h3 className="relative text-xl font-bold text-foreground">{title}</h3>
      <p className="relative mt-2 leading-relaxed text-muted-foreground">
        {description}
      </p>
    </Link>
  );
}

/**
 * A coloured callout box. The rim is an inset ring rather than a border so the
 * box sits on the page like the other panels (a 1px border plus a flat tint
 * read as a form field next to them).
 *
 * `heading` is optional: most callouts are a single sentence, but the guide's
 * "Looking for API details?" box is a titled aside, as in the legacy page.
 */
export function Callout({
  variant = "info",
  heading,
  children,
}: {
  variant?: "info" | "warning" | "success";
  heading?: string;
  children: React.ReactNode;
}) {
  const styles = {
    // Flat washes on the shared /10 wash step (see the colour-role ladder in
    // DESIGN_SYSTEM.md); these were 120deg gradients until 2026-08-03.
    info: "bg-primary/10 shadow-[inset_0_0_0_1px_hsl(var(--primary)/0.28)]",
    warning: "bg-warning/10 shadow-[inset_0_0_0_1px_hsl(var(--warning)/0.28)]",
    success: "bg-success/10 shadow-[inset_0_0_0_1px_hsl(var(--success)/0.28)]",
  } as const;
  const headingTone = {
    info: "text-primary-text",
    warning: "text-warning-text",
    success: "text-success-text",
  } as const;
  return (
    <div className={cn("rounded-2xl p-5 text-sm", styles[variant])}>
      {heading ? (
        <h3 className={cn("mb-1.5 font-bold", headingTone[variant])}>
          {heading}
        </h3>
      ) : null}
      <div className="leading-relaxed text-foreground/90">{children}</div>
    </div>
  );
}

/**
 * One distinct colour per HTTP verb, tinted rather than solid-filled: on a dark
 * endpoint header a solid block of colour shouts louder than the path it
 * labels, which is the thing you actually read. PATCH deliberately shares
 * PUT's amber — they're the same kind of operation.
 *
 * Fixed tones rather than the `--info`/`--success`/… tokens: this badge only
 * ever renders on the always-dark request bar, so a theme-following colour is
 * wrong by construction — in light mode the tokens darken and drop under
 * contrast on that dark bar. These are pinned bright enough to pass AA on it
 * (verified with axe in both themes).
 *
 * Held as bare HSL channels rather than hex so the same value can drive the
 * badge fill, its ring, its glow, and the endpoint bar's left rule at different
 * alphas — one hue per verb, declared once.
 *
 * Re-picked from the Polymarket ramps (2026-07-31), then lifted one step each
 * (2026-08-03) because the earlier check measured the badge against its own tint
 * over `--card` alone. The badge actually sits INSIDE the endpoint bar, which
 * carries its own `--method` tint, so the real composited background is a step
 * lighter than that calculation assumed and three of the four verbs failed AA in
 * situ: GET 4.13:1, POST 4.18:1, DELETE 4.01:1. Measured against the RENDERED
 * background they now read GET 5.22:1 (on #1d3e56) · POST 4.84:1 (on #224233) ·
 * PUT 7.26:1 · DELETE 5.63:1 (on #463639). If you retune these, sample the
 * rendered pixel — a tint-over-tint stack is not what a single mix() predicts.
 */
export const METHOD_HUES: Record<HttpMethod, string> = {
  GET: "205.1 98.8% 67.3%", // #59b9fe — Polymarket brand-700
  POST: "142.1 42.2% 55.9%", // #5fbe82 — green-600
  PUT: "49 93% 55%", // #f7d022 — yellow-500 (7.26:1, already clear)
  PATCH: "49 93% 55%",
  DELETE: "0 42.9% 76.7%", // #ddaaaa — red-800
};

/**
 * The verb's hue as a CSS custom property, for anything that needs to tint
 * itself to match the badge it sits beside (the endpoint bar's left rule).
 */
export function methodStyle(method: HttpMethod): React.CSSProperties {
  return { "--method": METHOD_HUES[method] } as React.CSSProperties;
}

export function HttpMethodBadge({ method }: { method: HttpMethod }) {
  return (
    <span
      style={methodStyle(method)}
      className="inline-block shrink-0 rounded-md bg-[hsl(var(--method)/0.15)] px-2 py-1 font-mono text-[0.6875rem] font-bold uppercase leading-none tracking-[0.06em] text-[hsl(var(--method))] ring-1 ring-inset ring-[hsl(var(--method)/0.25)]"
    >
      {method}
    </span>
  );
}

/**
 * The request line for one endpoint: the verb badge and the path on a bar
 * tinted by that verb, with a left rule in the same hue, so a GET block and a
 * DELETE block stay distinguishable while scrolling before you read either one.
 *
 * Shared by the v1 API renderer and the automation REST reference. Both legacy
 * pages carried their own copy of this header; keeping one component here is
 * what stops the two API surfaces from drifting apart again.
 */
export function EndpointHeader({
  method,
  path,
}: {
  method: HttpMethod;
  path: string;
}) {
  return (
    <div
      style={methodStyle(method)}
      className="relative flex items-center gap-3 border-b border-hairline bg-[hsl(var(--method)/0.10)] px-4 py-3"
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[3px] bg-[hsl(var(--method))]"
      />
      <HttpMethodBadge method={method} />
      <code className="break-all font-mono text-[0.8125rem] font-medium tracking-[-0.01em] text-foreground">
        {path}
      </code>
    </div>
  );
}

/**
 * 2xx success · 3xx info · 4xx caller error · 5xx server error. Tones come from
 * the `--docs-*` ramp, which is AA-safe as tinted text in both themes (see the
 * token block in globals.css). The glow is keyed to the same hue, so severity
 * reads from across the page before the digits resolve.
 */
function statusTone(statusCode: number | string): string {
  const n = Number.parseInt(String(statusCode), 10);
  if (n >= 200 && n < 300)
    return "bg-docs-2xx/15 text-docs-2xx ring-docs-2xx/25 shadow-[0_0_14px_-3px_hsl(var(--docs-2xx)/0.6)]";
  if (n >= 300 && n < 400)
    return "bg-docs-3xx/15 text-docs-3xx ring-docs-3xx/25 shadow-[0_0_14px_-3px_hsl(var(--docs-3xx)/0.6)]";
  if (n >= 400 && n < 500)
    return "bg-docs-4xx/15 text-docs-4xx ring-docs-4xx/25 shadow-[0_0_14px_-3px_hsl(var(--docs-4xx)/0.6)]";
  if (n >= 500)
    return "bg-docs-5xx/15 text-docs-5xx ring-docs-5xx/25 shadow-[0_0_14px_-3px_hsl(var(--docs-5xx)/0.6)]";
  return "bg-muted text-muted-foreground ring-border";
}

export function StatusCodeBadge({
  statusCode,
}: {
  statusCode: number | string;
}) {
  return (
    <span
      className={cn(
        "inline-block shrink-0 rounded-md px-2 py-1 font-mono text-[0.6875rem] font-bold tabular-nums leading-none ring-1 ring-inset",
        statusTone(statusCode),
      )}
    >
      {statusCode}
    </span>
  );
}

/* Shared table chrome: hairline dividers instead of the default heavier
 * `border-border`, a quiet uppercase header, and roomier cells. Docs tables are
 * scanned, not read — the row content should carry the contrast, not the grid. */
export const docTable = {
  wrap: "overflow-hidden rounded-2xl border border-hairline bg-card",
  head: "border-b border-hairline bg-elevated px-4 py-3 text-left text-[0.625rem] font-semibold uppercase tracking-[0.16em] text-docs-label",
  row: "border-b border-hairline transition-colors last:border-0 hover:bg-elevated",
  cell: "px-4 py-3.5 align-top text-[0.8125rem]",
  mono: "px-4 py-3.5 align-top font-mono text-[0.8125rem]",
  sectionTitle: "text-sm font-semibold tracking-tight text-foreground",
} as const;

/**
 * A parameter/field name. The chip is what separates an identifier you type
 * verbatim from the prose around it — mono alone doesn't survive a dense table.
 */
export function FieldName({ children }: { children: React.ReactNode }) {
  return (
    <code className="inline-block rounded-[5px] border border-hairline bg-elevated px-1.5 py-0.5 font-mono text-[0.75rem] font-medium text-foreground">
      {children}
    </code>
  );
}

/**
 * Required/optional as a badge rather than the words "Yes"/"No" — the column is
 * scanned, and a shape+colour resolves faster than reading. Amber (not red) for
 * required: a required field is a constraint to notice, not an error.
 *
 * The word is kept as the label rather than replaced with a dot/icon so the
 * column still reads correctly to a screen reader and in a plain-text copy.
 */
export function RequiredBadge({ required }: { required: boolean }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold ring-1 ring-inset",
        required
          ? "bg-docs-4xx/15 text-docs-4xx ring-docs-4xx/25"
          : "bg-docs-optional/10 text-docs-optional ring-docs-optional/25",
      )}
    >
      {required ? "Yes" : "No"}
    </span>
  );
}

export function ParameterTable({
  title,
  parameters,
}: {
  title: string;
  parameters: Parameter[];
}) {
  return (
    <div className="space-y-2.5">
      <h4 className={docTable.sectionTitle}>{title}</h4>
      <div className={docTable.wrap}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={docTable.head}>Name</TableHead>
              <TableHead className={docTable.head}>Type</TableHead>
              <TableHead className={docTable.head}>Required</TableHead>
              <TableHead className={docTable.head}>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parameters.map((p) => (
              <TableRow key={p.name} className={docTable.row}>
                <TableCell className={docTable.cell}>
                  <FieldName>{p.name}</FieldName>
                </TableCell>
                <TableCell
                  className={cn(docTable.mono, "text-muted-foreground")}
                >
                  {p.type}
                </TableCell>
                <TableCell className={docTable.cell}>
                  <RequiredBadge required={p.required} />
                </TableCell>
                <TableCell
                  className={cn(
                    docTable.cell,
                    "leading-relaxed text-muted-foreground",
                  )}
                >
                  {p.description}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export function ResponseTable({ responses }: { responses: ApiResponse[] }) {
  return (
    <div className="space-y-2.5">
      <h4 className={docTable.sectionTitle}>Responses</h4>
      <div className={docTable.wrap}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={cn(docTable.head, "w-24")}>
                Status
              </TableHead>
              <TableHead className={docTable.head}>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {responses.map((r) => (
              <TableRow key={r.statusCode} className={docTable.row}>
                <TableCell className={docTable.cell}>
                  <StatusCodeBadge statusCode={r.statusCode} />
                </TableCell>
                <TableCell className={docTable.cell}>
                  <span className="leading-relaxed text-foreground">
                    {r.description}
                  </span>
                  {r.body ? (
                    <span className="mt-1.5 block font-mono text-xs leading-relaxed text-muted-foreground">
                      {r.body}
                    </span>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/** A property/field table (automation reference). */
export function PropertyList({
  title,
  properties,
}: {
  title?: string;
  properties: Property[];
}) {
  return (
    <div className="space-y-2.5">
      {title ? <h4 className={docTable.sectionTitle}>{title}</h4> : null}
      <div className={docTable.wrap}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={docTable.head}>Name</TableHead>
              <TableHead className={docTable.head}>Type</TableHead>
              <TableHead className={docTable.head}>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {properties.map((p) => (
              <TableRow key={p.name} className={docTable.row}>
                <TableCell className={docTable.cell}>
                  <FieldName>
                    {p.name}
                    {p.optional ? "?" : ""}
                  </FieldName>
                </TableCell>
                <TableCell
                  className={cn(docTable.mono, "text-muted-foreground")}
                >
                  {p.type}
                </TableCell>
                <TableCell
                  className={cn(
                    docTable.cell,
                    "leading-relaxed text-muted-foreground",
                  )}
                >
                  {p.description}
                  {p.availableFrom ? (
                    <Badge variant="warning" className="ml-1.5">
                      since {p.availableFrom}
                    </Badge>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/** A single method's reference documentation. */
export function MethodDoc({ method }: { method: MethodDocData }) {
  return (
    <section className="space-y-3 border-t py-6 first:border-t-0">
      <div className="flex flex-wrap items-center gap-2">
        {/* `tap()`, not `tap` — the legacy heading carries the call parens, and
         * they're what separates a method heading from the plain identifiers in
         * the parameter table below it. The `id` is the legacy anchor, so a
         * deep link to a single method keeps working. */}
        <h3 id={method.name} className="font-mono text-lg font-semibold">
          {method.name}()
        </h3>
        {method.since ? (
          <Badge variant="outline">since {method.since}</Badge>
        ) : null}
      </div>
      {/* No "Signature" title bar label: the legacy page renders the signature
       * block bare, directly under the method heading. Same for the examples
       * below — only a NAMED example ("Launch with fresh data") gets a label. */}
      <CodeBlock code={method.signature} />
      {/* `whitespace-pre-line`: a few descriptions (submitTaskToAnotherJob,
       * logcat) carry a blank-line-separated bullet list. The reference renders
       * them in a plain <p>, which collapses the list into one run-on
       * paragraph; single-line descriptions are unaffected either way. */}
      <p className="whitespace-pre-line text-sm text-muted-foreground">
        {method.description}
      </p>

      {method.parameters && method.parameters.length > 0 ? (
        <div className="space-y-2.5">
          {/* The legacy MethodDoc labels each of its three blocks — Parameters,
           * Returns, Examples. The v5 port dropped all three, which left a
           * method as an unlabelled run of a table, a sentence, and some code.
           * The headings are the structure; restore them. */}
          <h4 className={docTable.sectionTitle}>Parameters</h4>
          <div className={docTable.wrap}>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {/* "Name", as in the legacy table — not "Parameter", which
                   * repeats the heading now sitting directly above it. */}
                  <TableHead className={docTable.head}>Name</TableHead>
                  <TableHead className={docTable.head}>Type</TableHead>
                  <TableHead className={docTable.head}>Description</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {method.parameters.map((p) => (
                  <TableRow key={p.name} className={docTable.row}>
                    <TableCell className={docTable.cell}>
                      <FieldName>
                        {p.name}
                        {p.optional ? "?" : ""}
                      </FieldName>
                    </TableCell>
                    <TableCell
                      className={cn(docTable.mono, "text-muted-foreground")}
                    >
                      {p.type}
                    </TableCell>
                    <TableCell
                      className={cn(
                        docTable.cell,
                        "leading-relaxed text-muted-foreground",
                      )}
                    >
                      {p.description}
                      {p.defaultValue ? (
                        // Italic, not dimmed: `text-muted-foreground` here
                        // measures 2.72:1 in light mode, and the cell's own
                        // `--muted-foreground` has no headroom left to dim.
                        <span className="italic">
                          {" "}
                          (default: {p.defaultValue})
                        </span>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ) : null}

      {method.returnType ? (
        <div className="space-y-2.5">
          <h4 className={docTable.sectionTitle}>Returns</h4>
          {/* A tinted type chip followed by the prose, as in the legacy block —
           * not one inline "Returns X — description" sentence. The chip is what
           * makes the return type scannable against the description beside it. */}
          <p className="text-sm">
            <code className="rounded-md bg-primary/10 px-1.5 py-0.5 font-mono text-[0.8125rem] font-medium text-primary-text">
              {method.returnType.type}
            </code>
            <span className="ml-2 text-muted-foreground">
              {method.returnType.description}
            </span>
          </p>
        </div>
      ) : null}

      {method.examples && method.examples.length > 0 ? (
        <div className="space-y-2.5">
          <h4 className={docTable.sectionTitle}>Examples</h4>
          {method.examples.map((ex, i) => (
            <CodeBlock key={i} code={ex.code} title={ex.title} />
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** A named TypeScript type/interface block with an optional description. */
export function TypeBlock({
  name,
  code,
  description,
}: {
  name: string;
  code: string;
  description?: string;
}) {
  return (
    <div className="space-y-2">
      <h3 className="font-mono text-lg font-semibold">{name}</h3>
      {description ? (
        <p className="text-sm text-muted-foreground">{description}</p>
      ) : null}
      {/* Untitled, as in the legacy component — the `name` heading sits
       * directly above, so a title bar repeating it is a duplicate label. */}
      <CodeBlock code={code} />
    </div>
  );
}

/** A table of named constants (name · value · description). */
export function ConstantsList({
  title,
  constants,
}: {
  title?: string;
  constants: Constant[];
}) {
  return (
    <div className="space-y-2.5">
      {title ? <h4 className={docTable.sectionTitle}>{title}</h4> : null}
      <div className={docTable.wrap}>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className={docTable.head}>Constant</TableHead>
              <TableHead className={docTable.head}>Value</TableHead>
              <TableHead className={docTable.head}>Description</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {constants.map((c) => (
              <TableRow key={c.name} className={docTable.row}>
                <TableCell className={cn(docTable.mono, "text-primary-text")}>
                  {c.name}
                </TableCell>
                <TableCell
                  className={cn(docTable.mono, "text-muted-foreground")}
                >
                  {typeof c.value === "string" ? `"${c.value}"` : c.value}
                </TableCell>
                <TableCell
                  className={cn(
                    docTable.cell,
                    "leading-relaxed text-muted-foreground",
                  )}
                >
                  {c.description}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
